import {
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { m } from '../paraglide/messages.js';
import { Icon, type IconName } from './Icon.tsx';
import { IconButton } from './IconButton.tsx';
import './TextField.css';

interface FieldFrame {
  label: string;
  /** Visually hide the label (it stays for screen readers). */
  hideLabel?: boolean;
  help?: ReactNode;
  /** Error message; marks the field invalid. */
  error?: string | null;
  icon?: IconName;
  /** Trailing IconButton (clear, paste …). */
  trailing?: ReactNode;
  mono?: boolean;
  className?: string;
}

function Frame({
  id,
  frame,
  area = false,
  children,
}: {
  id: string;
  frame: FieldFrame;
  area?: boolean;
  children: ReactNode;
}) {
  const { label, hideLabel, help, error, icon, trailing, mono, className = '' } = frame;
  const classes = ['field', error && 'is-error', mono && 'field--mono', className];
  return (
    <div className={classes.filter(Boolean).join(' ')}>
      <label htmlFor={id} className={hideLabel ? 'visually-hidden' : 'field__label'}>
        {label}
      </label>
      <div className={`field__control${area ? ' field__control--area' : ''}`}>
        {icon && <Icon name={icon} className="field__icon" />}
        {children}
        {trailing}
      </div>
      {error ? (
        <p className="field__help" id={`${id}-help`} role="alert">
          <Icon name="alert" size="s" />
          {error}
        </p>
      ) : (
        help && (
          <p className="field__help" id={`${id}-help`}>
            {help}
          </p>
        )
      )}
    </div>
  );
}

/**
 * DESIGN.md §10 TextField: always-visible label, 52 px control, helper or error text.
 * `masked` hides the value (API keys) with a reveal button.
 */
export function TextField({
  label,
  hideLabel,
  help,
  error,
  icon,
  trailing,
  mono,
  masked = false,
  className,
  id: givenId,
  ...input
}: FieldFrame & Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & { masked?: boolean }) {
  const generated = useId();
  const id = givenId ?? generated;
  const [revealed, setRevealed] = useState(false);
  const reveal = masked && (
    <IconButton
      icon="eye"
      label={revealed ? m.field_hide() : m.field_reveal()}
      aria-pressed={revealed}
      onClick={() => setRevealed(!revealed)}
    />
  );
  return (
    <Frame
      id={id}
      frame={{ label, hideLabel, help, error, icon, trailing: trailing ?? reveal, mono, className }}
    >
      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || help ? `${id}-help` : undefined}
        {...input}
        type={masked && !revealed ? 'password' : (input.type ?? 'text')}
      />
    </Frame>
  );
}

/** Multi-line TextField (ingredients, steps, pasted text). */
export function TextArea({
  label,
  hideLabel,
  help,
  error,
  className,
  id: givenId,
  ...textarea
}: FieldFrame & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'>) {
  const generated = useId();
  const id = givenId ?? generated;
  return (
    <Frame id={id} frame={{ label, hideLabel, help, error, className }} area>
      <textarea
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || help ? `${id}-help` : undefined}
        {...textarea}
      />
    </Frame>
  );
}

/** DESIGN.md §10 SearchField: 44 px, sunken, leading search icon, clear button when filled. */
export function SearchField({
  label,
  value,
  onChange,
  placeholder,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  return (
    <div className="search-field" role="search">
      <Icon name="search" />
      <input
        type="search"
        aria-label={label}
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        enterKeyHint="search"
        // Searches are recipe and ingredient names: autocorrect would change them.
        autoCorrect="off"
        spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <IconButton
          icon="close"
          iconSize="s"
          label={m.field_clear()}
          onClick={() => onChange('')}
        />
      )}
    </div>
  );
}
